import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react'
import { MappedLeadData } from '../../../../../types'
import { LeadReportModal } from '../../../../../components/LeadReportModal'
import { SkeletonLeadModal } from '../../../../../components/atoms/Skeleton'
import { useAuth } from '../../../../../contexts/AuthContext'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useClientProfile } from '../../../../../hooks/useClientProfile'
import { queryKeys } from '../../../../../services/query/queryKeys'
import { appwriteQueryFns } from '../../../../../services/query/appwriteQueryFns'
import { AppLayout } from '../../../../../components/layouts/AppLayout'
import { useContextCapture } from '../../../../../hooks/useOmnipresentAI'
import { getPreferredStatus } from '../../../../../utils/statusMapping'
import BuyCreditsModal from '../../../../../components/modals/BuyCreditsModal'
import InvoicesModal from '../../../../../components/modals/InvoicesModal'

// Valid tab names for lead modal
type LeadTab = 'overview' | 'person' | 'company' | 'signals' | 'network' | 'profile-intel' | 'outreach' | 'interactions'

interface LeadSearchParams {
  tab?: LeadTab
}

export const Route = createFileRoute('/campaigns/$campaignId/leads/$leadId')({
  validateSearch: (search: Record<string, unknown>): LeadSearchParams => ({
    tab: (search.tab as LeadTab) ?? 'overview',
  }),
  component: CampaignLeadPage,
})

function CampaignLeadPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { campaignId, leadId } = Route.useParams()
  const { tab } = Route.useSearch()
  const { currentUser, targetEmail: impersonatedEmail } = useAuth()
  // Use impersonated email if set (admin viewing as another user), otherwise current user's email
  const targetEmail = impersonatedEmail || currentUser?.email
  const clientQuery = useClientProfile(targetEmail)

  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false)
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false)

  // Context Capture for Omnipresent AI
  const { capture } = useContextCapture()

  const cachedCampaignLeads = queryClient.getQueryData<MappedLeadData[]>(
    queryKeys.leads.byCampaign(campaignId, targetEmail)
  )
  const cachedLeadFromCampaign = cachedCampaignLeads?.find((item) => String(item.id) === leadId)

  // Use TanStack Query to load lead data (reactive to cache updates)
  const leadQuery = useQuery({
    queryKey: queryKeys.leads.detail(campaignId, leadId),
    queryFn: async () => {
      const detailLead = await appwriteQueryFns.leads.detail({
        campaignId,
        leadId,
        userEmail: targetEmail!,
      })

      if (!cachedLeadFromCampaign) return detailLead

      return {
        ...detailLead,
        status: getPreferredStatus(cachedLeadFromCampaign.status, detailLead?.status) || detailLead?.status,
      }
    },
    placeholderData: cachedLeadFromCampaign,
    enabled: !!leadId && !!currentUser?.email,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const lead = leadQuery.data
  const isLoading = leadQuery.isPending
  const error = leadQuery.error?.message || null

  useEffect(() => {
    if (!lead) return

    const syncLeadStatus = (cacheKey: readonly unknown[]) => {
      queryClient.setQueryData<MappedLeadData[] | undefined>(cacheKey, (existingLeads) => {
        if (!existingLeads) return existingLeads

        return existingLeads.map((item) => {
          if (String(item.id) !== leadId) return item

          return {
            ...item,
            status: getPreferredStatus(item.status, lead.status) || lead.status,
          }
        })
      })
    }

    syncLeadStatus(queryKeys.leads.byCampaign(campaignId))
    syncLeadStatus(queryKeys.leads.byCampaign(campaignId, targetEmail))
  }, [lead, leadId, campaignId, targetEmail, queryClient])

  // Capture context when lead loaded
  useEffect(() => {
    if (lead) {
      capture('lead-detail', {
        leadId: lead.id,
        campaignId: lead.campaignId,
        name: lead.leadName,
        role: lead.leadRole,
        company: lead.companyName,
        status: lead.status,
        icp: lead.icpScore,
        summary: lead.leadExecutiveOverview,
        fullLeadData: {
           person: lead.leadName,
           company: lead.companyName,
           industry: lead.companyIndustry,
           companySize: lead.companySize,
           location: lead.companyCountry,
           website: lead.companyWebsite
        }
      })
    }
  }, [lead, capture])

  // Handle modal close - navigate back to campaign
  const handleClose = () => {
    navigate({
      to: '/campaigns/$campaignId',
      params: { campaignId },
    })
  }

  // Handle tab change - update URL
  const handleTabChange = (newTab: string) => {
    navigate({
      to: '/campaigns/$campaignId/leads/$leadId',
      params: { campaignId, leadId },
      search: { tab: newTab as LeadTab },
    })
  }

  return (
    <AppLayout
      currentView="campaigns"
      onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      onViewInvoices={() => setIsInvoicesModalOpen(true)}
      aiCredits={clientQuery.data?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      clientInfo={clientQuery.data}
      isLoadingLead={isLoading}
    >
      {/* Loading State */}
      {isLoading && <SkeletonLeadModal />}

      {/* Error State */}
      {error && !isLoading && (
        <div className="min-h-screen flex items-center justify-center pt-28">
          <div className="text-center">
            <div className="mb-4 p-4 rounded-2xl bg-red-500/5 border border-red-500/10 text-red-500 inline-block">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Error Loading Lead</h2>
            <p className="text-sm text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => navigate({ to: '/campaigns/$campaignId', params: { campaignId } })}
              className="px-6 py-2 bg-[#5260FF] hover:bg-[#5260FF]/80 text-white rounded-lg text-sm font-medium"
            >
              Back to Campaign
            </button>
          </div>
        </div>
      )}

      {/* Lead Report Modal */}
      {lead && <LeadReportModal lead={lead} onClose={handleClose} campaignId={campaignId} />}

      {/* Buy Credits Modal */}
      <BuyCreditsModal isOpen={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />

      {/* Invoices Modal */}
      <InvoicesModal isOpen={isInvoicesModalOpen} onClose={() => setIsInvoicesModalOpen(false)} />
    </AppLayout>
  )
}
